import React, { useState, useEffect } from 'react';
import {
  ArrowRight,
  ArrowLeft,
  User,
  Mail,
  Phone,
  MapPin,
  Home,
  Calendar,
  Clock,
  ClipboardList,
  Check,
} from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { pagePadding } from '../styles/layout';
import {
  GOLD,
  GOLD_RGBA,
  BG,
  CARD_BG,
  INPUT_BG,
  MIN_BOOKING_HOURS,
  calculateBookingPricing,
  getBookingDurationHours,
} from '../constants/booking';
import { formatCalendarDate } from '../utils/bookingDates';

const ARIZONA_CITIES = [
  'Apache Junction',
  'Avondale',
  'Cave Creek',
  'Chandler',
  'Fountain Hills',
  'Gilbert',
  'Goodyear',
  'Mesa',
  'Paradise Valley',
  'Peoria',
  'Phoenix',
  'Queen Creek',
  'Santan Valley',
  'Scottsdale',
  'Surprise',
  'Tempe',
];

const SURFACE_TYPES = [
  'Hardwood / Tile',
  'Carpet',
  'Artificial Turf',
  'Real Grass / Dirt',
  'Other',
];

const VENUE_SETTINGS = ['Indoor', 'Outdoor', 'Other'];

const BG_DARK = INPUT_BG;

const inputBaseStyle = {
  width: '100%',
  boxSizing: 'border-box',
  backgroundColor: BG_DARK,
  color: '#F5F0E8',
  border: '1px solid #4b5563',
  borderRadius: '0.75rem',
  padding: '0.875rem 1rem',
  fontSize: '1rem',
  outline: 'none',
  transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
};

const labelStyle = {
  display: 'block',
  fontSize: '0.8125rem',
  fontWeight: '600',
  color: '#F5F0E8',
  marginBottom: '0.5rem',
  letterSpacing: '0.02em',
  textTransform: 'uppercase',
};

const hintStyle = {
  fontSize: '0.75rem',
  color: '#9ca3af',
  marginTop: '0.35rem',
  lineHeight: 1.5,
};

const warningStyle = {
  fontSize: '0.75rem',
  color: '#fbbf24',
  marginTop: '0.5rem',
  lineHeight: 1.5,
};

const handleInputFocus = (e) => {
  e.target.style.borderColor = GOLD;
  e.target.style.boxShadow = `0 0 0 2px rgba(201, 168, 76, 0.2)`;
};

const handleInputBlur = (e) => {
  e.target.style.borderColor = '#4b5563';
  e.target.style.boxShadow = 'none';
};

const SectionHeader = ({ icon: Icon, title }) => (
  <div style={{ marginBottom: '1.5rem' }}>
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        marginBottom: '0.75rem',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '2.5rem',
          height: '2.5rem',
          borderRadius: '0.75rem',
          backgroundColor: `${GOLD_RGBA}, 0.12)`,
          border: `1px solid ${GOLD_RGBA}, 0.35)`,
        }}
      >
        <Icon size={20} color={GOLD} />
      </div>
      <h2
        style={{
          fontSize: '1.375rem',
          fontWeight: '700',
          color: '#F5F0E8',
          margin: 0,
        }}
      >
        {title}
      </h2>
    </div>
    <div style={{ height: '1px', background: `linear-gradient(90deg, ${GOLD}, transparent)` }} />
  </div>
);

const RequirementCard = ({ checked, onChange, label }) => (
  <button
    type="button"
    onClick={onChange}
    style={{
      width: '100%',
      textAlign: 'left',
      display: 'flex',
      alignItems: 'flex-start',
      gap: '1rem',
      padding: '1rem 1.25rem',
      borderRadius: '0.75rem',
      cursor: 'pointer',
      backgroundColor: checked ? 'rgba(201, 168, 76, 0.1)' : BG_DARK,
      border: checked ? `2px solid ${GOLD}` : '1px solid #4b5563',
      transition: 'all 0.2s ease',
    }}
  >
    <div
      style={{
        flexShrink: 0,
        width: '1.5rem',
        height: '1.5rem',
        borderRadius: '0.375rem',
        border: checked ? `2px solid ${GOLD}` : '2px solid #6b7280',
        backgroundColor: checked ? GOLD : 'transparent',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginTop: '0.125rem',
      }}
    >
      {checked && <Check size={14} color="#000000" strokeWidth={3} />}
    </div>
    <span style={{ fontWeight: '500', color: '#F5F0E8', lineHeight: 1.5 }}>
      {label}
    </span>
  </button>
);

const PickChip = ({ selected, onClick, label }) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      padding: '0.625rem 1rem',
      borderRadius: '9999px',
      fontSize: '0.8125rem',
      fontWeight: '600',
      cursor: 'pointer',
      whiteSpace: 'nowrap',
      backgroundColor: selected ? GOLD : BG_DARK,
      color: selected ? '#000000' : '#F5F0E8',
      border: selected ? `2px solid ${GOLD}` : '1px solid #4b5563',
      transition: 'all 0.2s ease',
    }}
  >
    {label}
  </button>
);

const CustomerInfo = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(false);

  // Get booking data from location state
  const bookingData = location.state?.bookingData;

  // Check for mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState('');

  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phoneNumber: '',
    venueStreet: '',
    venueCity: '',
    venueState: 'Arizona',
    venueZip: '',
    apartmentNumber: '',
    gateCode: '',
    surfaceType: '',
    surfaceTypeOther: '',
    venueSetting: '',
    venueSettingOther: '',
    confirmedCeilingHeight: false,
    estimatedGuestCount: '',
    dayOfContactName: '',
    dayOfContactPhone: '',
    hasFlatSpace: false,
    hasOutlet: false,
    hasAccess: false,
    venueNotes: '',
  });

  // Redirect if no booking data
  useEffect(() => {
    if (!bookingData) {
      navigate('/booking');
    }
  }, [bookingData, navigate]);

  const calculateTotal = () => {
    if (!bookingData) return { total: 0, deposit: 0 };
    const durationHours = getBookingDurationHours(bookingData);
    const { total, deposit } = calculateBookingPricing(
      bookingData.selectedPackage,
      durationHours,
      bookingData.selectedAddOns || []
    );
    return { total, deposit };
  };

  const { total, deposit } = calculateTotal();

  const durationHours =
    bookingData?.durationHours ??
    (bookingData?.selectedPackage?.baseHours || MIN_BOOKING_HOURS) + (bookingData?.extraHours || 0);

  const handleFormSubmit = async (event) => {
    event.preventDefault();

    if (!bookingData) {
      setError('No booking data found. Please start over.');
      return;
    }

    // Validate form
    if (!formData.fullName.trim()) {
      setError('Full name is required');
      return;
    }
    if (!formData.email.trim() || !formData.email.includes('@')) {
      setError('Valid email address is required');
      return;
    }
    if (!formData.phoneNumber.trim()) {
      setError('Phone number is required');
      return;
    }
    if (!formData.venueStreet.trim()) {
      setError('Venue street address is required');
      return;
    }
    if (!formData.venueCity.trim()) {
      setError('Please select a city from the list');
      return;
    }
    if (!ARIZONA_CITIES.includes(formData.venueCity)) {
      setError('We only serve the cities listed in the dropdown');
      return;
    }
    if (!formData.venueZip.trim()) {
      setError('ZIP code is required');
      return;
    }
    if (!/^\d{5}(-\d{4})?$/.test(formData.venueZip.trim())) {
      setError('Please enter a valid ZIP code (5 digits or 5+4 format)');
      return;
    }
    if (!formData.surfaceType) {
      setError('Please select a surface type');
      return;
    }
    if (formData.surfaceType === 'Other' && !formData.surfaceTypeOther.trim()) {
      setError('Please describe the surface type');
      return;
    }
    if (!formData.venueSetting) {
      setError('Please select a venue setting');
      return;
    }
    if (formData.venueSetting === 'Other' && !formData.venueSettingOther.trim()) {
      setError('Please describe the venue setting');
      return;
    }
    if (!formData.confirmedCeilingHeight) {
      setError('Please confirm the venue has 12 ft+ ceilings or is outdoors');
      return;
    }
    const guestCount = Number.parseInt(formData.estimatedGuestCount, 10);
    if (!Number.isFinite(guestCount) || guestCount < 1) {
      setError('Please enter a valid estimated guest count');
      return;
    }
    if (!formData.dayOfContactName.trim()) {
      setError('Day-of contact name is required');
      return;
    }
    if (!formData.dayOfContactPhone.trim()) {
      setError('Day-of contact phone number is required');
      return;
    }
    if (!formData.hasFlatSpace || !formData.hasOutlet || !formData.hasAccess) {
      setError('All venue requirements must be confirmed');
      return;
    }

    setError(null);

    // Navigate to payment page with all data
    navigate('/payment', {
      state: {
        bookingData,
        customerInfo: {
          ...formData,
          estimatedGuestCount: Number.parseInt(formData.estimatedGuestCount, 10),
          venueLocation: `${formData.venueStreet}${formData.apartmentNumber ? `, ${formData.apartmentNumber}` : ''}, ${formData.venueCity}, ${formData.venueState} ${formData.venueZip}${formData.gateCode ? ` (Gate Code: ${formData.gateCode})` : ''}`,
          billingAddress: `${formData.venueStreet}${formData.apartmentNumber ? `, ${formData.apartmentNumber}` : ''}, ${formData.venueCity}, ${formData.venueState} ${formData.venueZip}${formData.gateCode ? ` (Gate Code: ${formData.gateCode})` : ''}`,
        },
      },
    });
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleCheckboxChange = (field) => {
    setFormData((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const handleRadioChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
      ...(field === 'surfaceType' && value !== 'Other' ? { surfaceTypeOther: '' } : {}),
      ...(field === 'venueSetting' && value !== 'Other' ? { venueSettingOther: '' } : {}),
    }));
  };

  const cardPadding = isMobile ? '1.5rem' : '2rem';

  const renderBookingSummary = () => (
    <div
      style={{
        backgroundColor: CARD_BG,
        borderRadius: '1rem',
        padding: cardPadding,
        border: `1px solid rgba(201, 168, 76, 0.3)`,
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          marginBottom: '1.25rem',
          paddingBottom: '1rem',
          borderBottom: `1px solid rgba(201, 168, 76, 0.25)`,
        }}
      >
        <ClipboardList size={22} color={GOLD} />
        <h2 style={{ fontSize: '1.25rem', fontWeight: '700', color: '#F5F0E8', margin: 0 }}>
          Booking Summary
        </h2>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <div>
          <div
            style={{
              fontSize: '0.75rem',
              fontWeight: '600',
              color: '#F5F0E8',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              marginBottom: '0.25rem',
            }}
          >
            Package
          </div>
          <div style={{ fontSize: '1.0625rem', fontWeight: '600', color: '#F5F0E8' }}>
            {bookingData.selectedPackage.name}
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
          <Calendar size={18} color={GOLD} style={{ flexShrink: 0, marginTop: '0.125rem' }} />
          <div>
            <div style={{ fontSize: '0.9375rem', color: '#F5F0E8' }}>
              {formatCalendarDate(bookingData.selectedDate, {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
          <Clock size={18} color={GOLD} style={{ flexShrink: 0, marginTop: '0.125rem' }} />
          <div style={{ fontSize: '0.9375rem', color: '#F5F0E8' }}>{bookingData.selectedTime}</div>
        </div>

        <div>
          <div
            style={{
              fontSize: '0.75rem',
              fontWeight: '600',
              color: '#F5F0E8',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              marginBottom: '0.25rem',
            }}
          >
            Duration
          </div>
          <div style={{ fontSize: '0.9375rem', color: '#F5F0E8' }}>
            {durationHours} hour{durationHours !== 1 ? 's' : ''}
            {bookingData.extraHours > 0 && (
              <span style={{ color: '#F5F0E8' }}>
                {' '}
                ({bookingData.extraHours} additional hour
                {bookingData.extraHours > 1 ? 's' : ''} beyond base)
              </span>
            )}
          </div>
        </div>

        <div>
          <div
            style={{
              fontSize: '0.75rem',
              fontWeight: '600',
              color: '#F5F0E8',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              marginBottom: '0.5rem',
            }}
          >
            Add-ons
          </div>
          {bookingData.selectedAddOns?.length > 0 ? (
            <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
              {bookingData.selectedAddOns.map((addon, index) => (
                <li
                  key={index}
                  style={{
                    fontSize: '0.875rem',
                    color: '#F5F0E8',
                    padding: '0.35rem 0',
                    borderBottom:
                      index < bookingData.selectedAddOns.length - 1
                        ? '1px solid rgba(75, 85, 99, 0.4)'
                        : 'none',
                  }}
                >
                  {addon.name}
                  {addon.price > 0 && (
                    <span style={{ color: '#F5F0E8' }}> — ${addon.price}</span>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <p style={{ margin: 0, fontSize: '0.875rem', color: '#6b7280' }}>None selected</p>
          )}
        </div>
      </div>

      <div
        style={{
          marginTop: '1.5rem',
          paddingTop: '1.25rem',
          borderTop: `1px solid rgba(201, 168, 76, 0.25)`,
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            marginBottom: '0.5rem',
          }}
        >
          <span style={{ fontSize: '1rem', fontWeight: '600', color: '#F5F0E8' }}>Total</span>
          <span style={{ fontSize: '1.5rem', fontWeight: '700', color: '#F5F0E8' }}>${total}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <span style={{ fontSize: '0.875rem', color: '#F5F0E8' }}>Deposit due today (25%)</span>
          <span style={{ fontSize: '1.125rem', fontWeight: '700', color: GOLD }}>${deposit}</span>
        </div>
      </div>
    </div>
  );

  if (!bookingData) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: BG,
          padding: pagePadding(isMobile),
        }}
      >
        <div
          style={{
            textAlign: 'center',
            backgroundColor: CARD_BG,
            borderRadius: '1rem',
            padding: '2.5rem',
            border: '1px solid rgba(201, 168, 76, 0.25)',
            maxWidth: '28rem',
          }}
        >
          <h2 style={{ color: '#F5F0E8', marginBottom: '1rem', fontWeight: '600' }}>
            No booking data found
          </h2>
          <p style={{ color: '#F5F0E8', marginBottom: '2rem' }}>
            Please start your booking process again.
          </p>
          <button
            type="button"
            onClick={() => navigate('/booking')}
            style={{
              backgroundColor: GOLD,
              color: '#000000',
              border: 'none',
              borderRadius: '0.75rem',
              padding: '0.75rem 1.5rem',
              fontSize: '1rem',
              fontWeight: '700',
              cursor: 'pointer',
            }}
          >
            Start Booking
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="dd-page dd-customer-info"
      style={{
        backgroundColor: BG,
        padding: pagePadding(isMobile),
      }}
    >
      <div style={{ maxWidth: '80rem', margin: '0 auto', width: '100%' }}>
        {/* Page header */}
        <div style={{ marginBottom: '2rem' }}>
          <button
            type="button"
            onClick={() => navigate('/booking')}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              backgroundColor: 'transparent',
              border: '1px solid #4b5563',
              borderRadius: '0.5rem',
              padding: '0.5rem 1rem',
              fontSize: '0.875rem',
              fontWeight: '600',
              color: '#F5F0E8',
              cursor: 'pointer',
              marginBottom: '1.5rem',
            }}
          >
            <ArrowLeft size={16} color={GOLD} />
            Back to Booking
          </button>

          <div className="dd-page-intro" style={{ marginBottom: 0, paddingLeft: 0 }}>
            <h1>Complete Your Booking</h1>
            <p style={{ maxWidth: '36rem' }}>
              Share your contact and venue details to secure your reservation. A 25% deposit is
              due at checkout.
            </p>
          </div>
        </div>

        <div
          style={{
            display: isMobile ? 'flex' : 'grid',
            flexDirection: isMobile ? 'column' : undefined,
            gridTemplateColumns: isMobile ? undefined : 'minmax(min(17.5rem, 100%), 21.25rem) 1fr',
            gap: '2rem',
            alignItems: 'start',
            width: '100%',
            boxSizing: 'border-box',
          }}
        >
          {/* Left: sticky booking summary */}
          <aside
            style={{
              position: isMobile ? 'static' : 'sticky',
              top: isMobile ? undefined : '6rem',
              width: '100%',
            }}
          >
            {renderBookingSummary()}
          </aside>

          {/* Right: form */}
          <form
            onSubmit={handleFormSubmit}
            style={{
              display: 'flex',
              flexDirection: 'column',
              height: 'auto',
            }}
          >
            {/* Contact Information */}
            <div
              style={{
                backgroundColor: CARD_BG,
                borderRadius: '1rem',
                padding: cardPadding,
                border: '1px solid rgba(201, 168, 76, 0.2)',
                marginBottom: '1.5rem',
              }}
            >
              <SectionHeader icon={User} title="Contact Information" />

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                  gap: '1.25rem',
                }}
              >
                <div style={{ gridColumn: isMobile ? 'auto' : '1 / -1' }}>
                  <label style={labelStyle}>Full Name *</label>
                  <input
                    type="text"
                    value={formData.fullName}
                    onChange={(e) => handleInputChange('fullName', e.target.value)}
                    onFocus={handleInputFocus}
                    onBlur={handleInputBlur}
                    style={inputBaseStyle}
                    placeholder="Enter your full name"
                    required
                  />
                </div>

                <div>
                  <label style={labelStyle}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                      <Mail size={14} color={GOLD} />
                      Email Address *
                    </span>
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    onFocus={handleInputFocus}
                    onBlur={handleInputBlur}
                    style={inputBaseStyle}
                    placeholder="you@example.com"
                    required
                  />
                </div>

                <div>
                  <label style={labelStyle}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                      <Phone size={14} color={GOLD} />
                      Phone Number *
                    </span>
                  </label>
                  <input
                    type="tel"
                    value={formData.phoneNumber}
                    onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                    onFocus={handleInputFocus}
                    onBlur={handleInputBlur}
                    style={inputBaseStyle}
                    placeholder="(555) 555-5555"
                    required
                  />
                </div>
              </div>
            </div>

            {/* Venue Information */}
            <div
              style={{
                backgroundColor: CARD_BG,
                borderRadius: '1rem',
                padding: cardPadding,
                border: '1px solid rgba(201, 168, 76, 0.2)',
                marginBottom: '1.5rem',
              }}
            >
              <SectionHeader icon={Home} title="Venue Information" />

              <div style={{ marginBottom: '1.75rem' }}>
                <h3
                  style={{
                    fontSize: '0.9375rem',
                    fontWeight: '600',
                    color: GOLD,
                    marginBottom: '1rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                  }}
                >
                  <MapPin size={16} />
                  Venue Address *
                </h3>

                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr 1fr',
                    gap: '1rem',
                    marginBottom: '1rem',
                  }}
                >
                  <div style={{ gridColumn: isMobile ? 'auto' : '1 / -1' }}>
                    <label style={labelStyle}>Street Address *</label>
                    <input
                      type="text"
                      value={formData.venueStreet}
                      onChange={(e) => handleInputChange('venueStreet', e.target.value)}
                      onFocus={handleInputFocus}
                      onBlur={handleInputBlur}
                      style={inputBaseStyle}
                      placeholder="123 Main Street"
                      required
                    />
                  </div>

                  <div>
                    <label style={labelStyle}>City *</label>
                    <select
                      value={formData.venueCity}
                      onChange={(e) => handleInputChange('venueCity', e.target.value)}
                      onFocus={handleInputFocus}
                      onBlur={handleInputBlur}
                      style={{
                        ...inputBaseStyle,
                        cursor: 'pointer',
                        appearance: 'none',
                      }}
                      required
                    >
                      <option value="" style={{ backgroundColor: BG_DARK }}>
                        Select City
                      </option>
                      {ARIZONA_CITIES.map((city) => (
                        <option key={city} value={city} style={{ backgroundColor: BG_DARK }}>
                          {city}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label style={labelStyle}>ZIP Code *</label>
                    <input
                      type="text"
                      value={formData.venueZip}
                      onChange={(e) => handleInputChange('venueZip', e.target.value)}
                      onFocus={handleInputFocus}
                      onBlur={handleInputBlur}
                      style={inputBaseStyle}
                      placeholder="85001"
                      pattern="[0-9]{5}(-[0-9]{4})?"
                      required
                    />
                  </div>
                </div>

                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                    gap: '1rem',
                  }}
                >
                  <div>
                    <label style={labelStyle}>Apartment / Unit</label>
                    <input
                      type="text"
                      value={formData.apartmentNumber}
                      onChange={(e) => handleInputChange('apartmentNumber', e.target.value)}
                      onFocus={handleInputFocus}
                      onBlur={handleInputBlur}
                      style={inputBaseStyle}
                      placeholder="Apt 123, Unit B, etc."
                    />
                  </div>

                  <div>
                    <label style={labelStyle}>Gate Code</label>
                    <input
                      type="text"
                      value={formData.gateCode}
                      onChange={(e) => handleInputChange('gateCode', e.target.value)}
                      onFocus={handleInputFocus}
                      onBlur={handleInputBlur}
                      style={inputBaseStyle}
                      placeholder="1234, #1234, etc."
                    />
                  </div>
                </div>
              </div>

              <div style={{ marginBottom: '1.75rem' }}>
                <label style={labelStyle}>Estimated Guest Count *</label>
                <input
                  type="number"
                  min="1"
                  value={formData.estimatedGuestCount}
                  onChange={(e) => handleInputChange('estimatedGuestCount', e.target.value)}
                  onFocus={handleInputFocus}
                  onBlur={handleInputBlur}
                  style={inputBaseStyle}
                  placeholder="e.g. 25"
                  required
                />
              </div>

              <div style={{ marginBottom: '1.75rem' }}>
                <label style={labelStyle}>Day-of Contact *</label>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                    gap: '1rem',
                  }}
                >
                  <div>
                    <label style={{ ...labelStyle, fontSize: '0.75rem' }}>Contact Name *</label>
                    <input
                      type="text"
                      value={formData.dayOfContactName}
                      onChange={(e) => handleInputChange('dayOfContactName', e.target.value)}
                      onFocus={handleInputFocus}
                      onBlur={handleInputBlur}
                      style={inputBaseStyle}
                      placeholder="On-site contact name"
                      required
                    />
                  </div>
                  <div>
                    <label style={{ ...labelStyle, fontSize: '0.75rem' }}>Contact Phone Number *</label>
                    <input
                      type="tel"
                      value={formData.dayOfContactPhone}
                      onChange={(e) => handleInputChange('dayOfContactPhone', e.target.value)}
                      onFocus={handleInputFocus}
                      onBlur={handleInputBlur}
                      style={inputBaseStyle}
                      placeholder="(555) 555-5555"
                      required
                    />
                  </div>
                </div>
                <p style={hintStyle}>Who should our team reach on event day?</p>
              </div>

              <div style={{ marginBottom: '1.75rem' }}>
                <h3
                  style={{
                    fontSize: '0.9375rem',
                    fontWeight: '600',
                    color: GOLD,
                    marginBottom: '1rem',
                  }}
                >
                  Venue Requirements *
                </h3>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                  <div>
                    <label style={labelStyle}>Surface Type *</label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                      {SURFACE_TYPES.map((option) => (
                        <PickChip
                          key={option}
                          selected={formData.surfaceType === option}
                          onClick={() => handleRadioChange('surfaceType', option)}
                          label={option}
                        />
                      ))}
                    </div>
                    {formData.surfaceType === 'Other' && (
                      <input
                        type="text"
                        value={formData.surfaceTypeOther}
                        onChange={(e) => handleInputChange('surfaceTypeOther', e.target.value)}
                        onFocus={handleInputFocus}
                        onBlur={handleInputBlur}
                        style={{ ...inputBaseStyle, marginTop: '0.75rem' }}
                        placeholder="Describe the surface"
                        required
                      />
                    )}
                  </div>

                  <div>
                    <label style={labelStyle}>Setting *</label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                      {VENUE_SETTINGS.map((option) => (
                        <PickChip
                          key={option}
                          selected={formData.venueSetting === option}
                          onClick={() => handleRadioChange('venueSetting', option)}
                          label={option}
                        />
                      ))}
                    </div>
                    {formData.venueSetting === 'Other' && (
                      <input
                        type="text"
                        value={formData.venueSettingOther}
                        onChange={(e) => handleInputChange('venueSettingOther', e.target.value)}
                        onFocus={handleInputFocus}
                        onBlur={handleInputBlur}
                        style={{ ...inputBaseStyle, marginTop: '0.75rem' }}
                        placeholder="Describe the setting"
                        required
                      />
                    )}
                  </div>

                  <div>
                    <label style={labelStyle}>Ceiling Height *</label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                      <PickChip
                        selected={formData.confirmedCeilingHeight}
                        onClick={() => handleCheckboxChange('confirmedCeilingHeight')}
                        label="12 ft+ ceilings or outdoors"
                      />
                    </div>
                    <p style={warningStyle}>
                      A minimum of 12 ft ceiling clearance is required for safe simulator operation.
                    </p>
                  </div>
                </div>

                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.75rem',
                    marginTop: '1.25rem',
                  }}
                >
                  <RequirementCard
                    checked={formData.hasFlatSpace}
                    onChange={() => handleCheckboxChange('hasFlatSpace')}
                    label="Flat space (15' x 15' minimum)"
                  />
                  <RequirementCard
                    checked={formData.hasOutlet}
                    onChange={() => handleCheckboxChange('hasOutlet')}
                    label="Electrical outlet within 50 feet"
                  />
                  <RequirementCard
                    checked={formData.hasAccess}
                    onChange={() => handleCheckboxChange('hasAccess')}
                    label="Easy access for equipment delivery (no stairs, narrow passages, or obstacles)"
                  />
                </div>
              </div>

              <div>
                <label style={labelStyle}>Additional Venue Notes</label>
                <textarea
                  value={formData.venueNotes}
                  onChange={(e) => handleInputChange('venueNotes', e.target.value)}
                  onFocus={handleInputFocus}
                  onBlur={handleInputBlur}
                  style={{
                    ...inputBaseStyle,
                    minHeight: '120px',
                    resize: 'vertical',
                    fontFamily: 'inherit',
                  }}
                  placeholder="Parking details, special instructions, or other venue-specific information..."
                />
              </div>
            </div>

            {error && (
              <div
                style={{
                  backgroundColor: 'rgba(239, 68, 68, 0.1)',
                  border: '1px solid rgba(239, 68, 68, 0.4)',
                  borderRadius: '0.75rem',
                  padding: '1rem',
                  color: '#fca5a5',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  marginBottom: '1rem',
                }}
              >
                {error}
              </div>
            )}

            {message && (
              <div
                style={{
                  backgroundColor: 'rgba(34, 197, 94, 0.1)',
                  border: '1px solid rgba(34, 197, 94, 0.35)',
                  borderRadius: '0.75rem',
                  padding: '1rem',
                  color: '#86efac',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  marginBottom: '1rem',
                }}
              >
                {message}
              </div>
            )}

            <div style={{ marginTop: 'auto', paddingTop: '0.5rem' }}>
              <button
                type="submit"
                disabled={isProcessing || !formData.confirmedCeilingHeight}
                style={{
                  width: '100%',
                  backgroundColor: GOLD,
                  color: '#000000',
                  border: 'none',
                  borderRadius: '0.75rem',
                  padding: '1.125rem 2rem',
                  fontSize: '1.125rem',
                  fontWeight: '700',
                  cursor: isProcessing ? 'not-allowed' : 'pointer',
                  opacity: isProcessing ? 0.7 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  letterSpacing: '0.01em',
                }}
              >
                {isProcessing ? (
                  <>
                    <div
                      style={{
                        width: '20px',
                        height: '20px',
                        border: '2px solid transparent',
                        borderTop: '2px solid #000000',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite',
                      }}
                    />
                    Processing...
                  </>
                ) : (
                  <>
                    Continue to Payment — ${deposit} Deposit
                    <ArrowRight size={20} />
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        select option {
          background-color: #1a1a1a;
          color: #f5f0e8;
        }
      `}</style>
    </div>
  );
};

export default CustomerInfo;
